import {
  COMPLETED_TASKS_LIMIT,
  SEARCH_KEYWORD_FLAGGED,
  TaskBasicData,
  TaskState,
  TaskType,
  isTaskTypeConcern,
  useGetTasksQuery,
} from "../features/data/patientApiSlice";
import {
  FilterType,
  FilterTypeInfo,
  QueryStateUpdateFn,
} from "../screens/Patients";
import { List, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";

import { SelectionSource } from "../features/api/logging";
import TaskListItem from "./TaskListItem";
import { createStyleSheet } from "../utils/styles";
import { useTranslation } from "react-i18next";

export const getTaskListItemId = (taskUid: string) => `TaskListItem_${taskUid}`;

const TaskList = (props: {
  showOnlyActiveTasks: boolean;
  filters: Set<FilterType>;
  searchTerm: string;
  selectedTaskUid: string;
  setTypeCounts: (typeCounts: Map<FilterType, FilterTypeInfo>) => void;
  onQueryStateUpdate: QueryStateUpdateFn;
  onTaskSelect: (
    patientUid: string,
    taskUid: string,
    selectionSource: SelectionSource
  ) => void;
  setListNavControls: (params: {
    navPrevious: () => void;
    navNext: () => void;
  }) => void;
  updateListNavState: (params: {
    canNavPrevious: boolean;
    canNavNext: boolean;
  }) => void;
}) => {
  const {
    showOnlyActiveTasks,
    filters,
    searchTerm,
    selectedTaskUid,
    setTypeCounts,
    onQueryStateUpdate,
    onTaskSelect,
    setListNavControls,
    updateListNavState,
  } = props;
  const { t } = useTranslation("Patients");

  // Component state
  const [tasksToDisplay, setTasksToDisplay] = useState<TaskBasicData[] | null>(
    null
  );

  // Queries/mutations
  const {
    data: tasks,
    isFetching,
    isError,
    error,
  } = useGetTasksQuery(
    {
      state: showOnlyActiveTasks ? TaskState.todo : TaskState.complete,
      filter: searchTerm,
    },
    {
      pollingInterval: 30000,
      skipPollingIfUnfocused: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );
  const { taskBasicData, taskLimitReached } = tasks || {};
  const unfilteredTaskBasicData = useGetTasksQuery(
    {
      state: showOnlyActiveTasks ? TaskState.todo : TaskState.complete,
      filter: "",
    },
    {
      pollingInterval: 30000,
      skipPollingIfUnfocused: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  ).data?.taskBasicData;

  // Callbacks
  const handlePreviousTask = useCallback(() => {
    const currentIndex = tasksToDisplay?.findIndex(
      (task) => task.uid === selectedTaskUid
    );
    if (currentIndex !== undefined && currentIndex > 0) {
      const previousTask = tasksToDisplay![currentIndex! - 1];
      onTaskSelect(
        previousTask.patient.uid,
        previousTask.uid,
        SelectionSource.NavButton
      );
    }
  }, [tasksToDisplay, selectedTaskUid, onTaskSelect]);
  const handleNextTask = useCallback(() => {
    const currentIndex = tasksToDisplay?.findIndex(
      (task) => task.uid === selectedTaskUid
    );
    if (
      currentIndex !== undefined &&
      currentIndex < tasksToDisplay!.length - 1
    ) {
      const nextTask = tasksToDisplay![currentIndex! + 1];
      onTaskSelect(
        nextTask.patient.uid,
        nextTask.uid,
        SelectionSource.NavButton
      );
    }
  }, [tasksToDisplay, selectedTaskUid, onTaskSelect]);
  const handleTaskClick = useCallback(
    (patientUid: string, taskUid: string) => {
      onTaskSelect(patientUid, taskUid, SelectionSource.TaskList);
    },
    [onTaskSelect]
  );

  // Reactive code
  useEffect(() => {
    setListNavControls({
      navPrevious: handlePreviousTask,
      navNext: handleNextTask,
    });
    return () => {
      setListNavControls({
        navPrevious: () => {},
        navNext: () => {},
      });
    };
  }, [setListNavControls, handlePreviousTask, handleNextTask]);

  useEffect(() => {
    const currentIndex =
      tasksToDisplay?.findIndex((task) => task.uid === selectedTaskUid) || -1;
    updateListNavState({
      canNavPrevious: currentIndex > 0,
      canNavNext: currentIndex < (tasksToDisplay?.length || 0) - 1,
    });
  }, [selectedTaskUid, updateListNavState, tasksToDisplay]);

  useEffect(() => {
    if (unfilteredTaskBasicData) {
      // Compute filter type counts
      const typeCountMap = new Map<FilterType, FilterTypeInfo>();
      unfilteredTaskBasicData?.forEach((basicTask) => {
        if (
          basicTask.type === TaskType.request_to_chat ||
          basicTask.type === TaskType.pending_hcw_message ||
          basicTask.type === TaskType.urgent_request_to_chat
        ) {
          typeCountMap.set(FilterType.ReadyToChat, {
            count: (typeCountMap.get(FilterType.ReadyToChat)?.count || 0) + 1,
            flaggedForSupervisor:
              typeCountMap.get(FilterType.ReadyToChat)?.flaggedForSupervisor ||
              basicTask.flagged_for_supervisor,
          });
        } else if (basicTask.type === TaskType.followup) {
          typeCountMap.set(FilterType.FollowUps, {
            count: (typeCountMap.get(FilterType.FollowUps)?.count || 0) + 1,
            flaggedForSupervisor:
              typeCountMap.get(FilterType.FollowUps)?.flaggedForSupervisor ||
              basicTask.flagged_for_supervisor,
          });
        } else {
          typeCountMap.set(FilterType.Concerns, {
            count: (typeCountMap.get(FilterType.Concerns)?.count || 0) + 1,
            flaggedForSupervisor:
              typeCountMap.get(FilterType.Concerns)?.flaggedForSupervisor ||
              basicTask.flagged_for_supervisor,
          });
        }
      });
      setTypeCounts(typeCountMap);
    }
  }, [unfilteredTaskBasicData, setTypeCounts]);

  useEffect(() => {
    if (taskBasicData) {
      // Compute task list to display
      const filteredTasks =
        taskBasicData?.filter(
          (basicTask, i) =>
            (!searchTerm ||
              searchTerm !== SEARCH_KEYWORD_FLAGGED ||
              basicTask.flagged_for_supervisor) &&
            ((filters.has(FilterType.ReadyToChat) &&
              (basicTask.type === TaskType.request_to_chat ||
                basicTask.type === TaskType.pending_hcw_message ||
                basicTask.type === TaskType.urgent_request_to_chat)) ||
              (filters.has(FilterType.FollowUps) &&
                basicTask.type === TaskType.followup) ||
              (filters.has(FilterType.Concerns) &&
                isTaskTypeConcern(basicTask.type)))
        ) || [];
      setTasksToDisplay(filteredTasks);
    }
  }, [taskBasicData, searchTerm, filters]);

  useEffect(() => {
    onQueryStateUpdate(
      (tasksToDisplay?.length || 0) === 0,
      isFetching,
      isError,
      error
    );
  }, [onQueryStateUpdate, isFetching, isError, error, tasksToDisplay]);

  return (
    <List sx={styles.listContainer}>
      {tasksToDisplay?.map((basicTask, i) => (
        <TaskListItem
          id={getTaskListItemId(basicTask.uid)}
          key={basicTask.uid}
          basicTask={basicTask}
          onClick={handleTaskClick}
          selected={basicTask.uid === selectedTaskUid}
        />
      ))}
      {taskLimitReached && (
        <Typography variant="body2" sx={styles.taskLimitReachedText}>
          {t("taskLimitReached", { count: COMPLETED_TASKS_LIMIT })}
        </Typography>
      )}
    </List>
  );
};

const styles = createStyleSheet({
  listContainer: {
    overflowY: "scroll",
    paddingY: 0,
  },
  taskLimitReachedText: {
    padding: 2,
    color: "GrayText",
  },
});

export default TaskList;
